// 貼り付けられた HTML 断片を、リンクを保ったプレーンテキストへ変換するユーティリティ。
// 目的: 表示テキスト付きリンク（<a href="URL">テキスト</a>）をコピーして付箋 / テキストの
// 編集領域（contentEditable）へ貼り付けたとき、表示テキストを失わずに [テキスト](URL) 形式で
// 取り込めるようにする（非編集表示では LinkifiedText が同形式を解釈してリンクとして描画する）。
// アンカー以外の要素は最小限のテキスト平坦化のみ行う。

import { splitTextIntoLinkSegments } from "./linkify-text";

/** ブロック要素として改行区切りにするタグ（HTML の構造をテキストの行へ落とすための最小集合） */
const BLOCK_TAGS = new Set([
  "address",
  "article",
  "blockquote",
  "div",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "li",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "tr",
  "ul",
]);

/** 出力へ含めないタグ（メタ情報・スタイル・スクリプト類） */
const SKIP_TAGS = new Set(["head", "meta", "noscript", "script", "style", "template", "title"]);

/**
 * http(s) の URL だけをリンクとして採用する（javascript: 等のスキーム混入を防ぐ）。
 * キャンバスの描画（linkify-text）は http(s) のみをリンク化するため、貼り付け側も http(s) に限定する。
 */
function isSafeHttpUrl(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** HTML 断片が保持可能な（http(s)）アンカーを 1 つ以上含むか */
function hasPreservableAnchor(doc: Document): boolean {
  return Array.from(doc.querySelectorAll("a[href]")).some((anchor) =>
    isSafeHttpUrl(anchor.getAttribute("href") ?? ""),
  );
}

/** [text](url) の text 側でリンク構文を壊す文字をエスケープする */
function escapeLinkText(text: string): string {
  return text.replace(/([\\[\]])/gu, "\\$1");
}

/**
 * href をそのまま [x](href) の URL 部へ埋め込んでも、表示側 parser（splitTextIntoLinkSegments）が
 * 同一の URL として往復できるか。均衡した括弧（例: /path_(v2)）は現行 parser がそのまま
 * 解釈できるため、エスケープ不要（( ) は reserved character のため、不要な %28/%29 化は
 * サーバによっては別リソース扱いになり得る）。
 */
function canRoundTripAsMarkdownLinkUrl(href: string): boolean {
  const segments = splitTextIntoLinkSegments(`[x](${href})`);
  return (
    segments.length === 1 &&
    segments[0]?.type === "link" &&
    segments[0].value === "x" &&
    segments[0].url === href
  );
}

/** [text](url) の url 側で、現行 parser では壊れる場合（空白・unmatched の括弧等）だけエスケープする */
function escapeLinkUrl(url: string): string {
  return canRoundTripAsMarkdownLinkUrl(url)
    ? url
    : url.replace(/ /gu, "%20").replace(/\(/gu, "%28").replace(/\)/gu, "%29");
}

/**
 * href を素の URL 文字列のまま出力しても、表示側の素 URL 検出（URL_REGEX + trimUrlTrailing）で
 * 同一の URL として往復できるか。末尾の閉じ括弧・約物や空白・CJK 文字を含む href は
 * 素の文字列だと別の URL へ再解釈される（例: "https://example.com/a)" は末尾 ) が落ちる）ため、
 * その場合は [text](URL) 形式（括弧・空白はパーセントエンコード）へ逃がす必要がある。
 */
function canRoundTripAsBareUrl(href: string): boolean {
  const segments = splitTextIntoLinkSegments(href);
  return (
    segments.length === 1 &&
    segments[0]?.type === "link" &&
    segments[0].url === href &&
    segments[0].value === href
  );
}

/** DOM ノードをリンク保持のテキストへ直列化する */
function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    // HTML の描画と同様に、ソース由来の連続空白・改行は 1 個の空白へ畳む。
    return (node.textContent ?? "").replace(/\s+/gu, " ");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }
  const element = node as Element;
  const tag = element.tagName.toLowerCase();
  if (SKIP_TAGS.has(tag)) {
    return "";
  }
  if (tag === "br") {
    return "\n";
  }
  if (tag === "a") {
    const href = element.getAttribute("href") ?? "";
    const text = serializeChildren(element).replace(/\s+/gu, " ").trim();
    if (!isSafeHttpUrl(href)) {
      return text;
    }
    // 表示テキストが URL そのもの（または空）の場合、素の URL 文字列として表示側と
    // 安全に往復できる href に限り素の URL のまま残す（自動リンク化・省略表示に任せる）。
    // 往復できない href（末尾括弧・空白入り等）は [text](URL) 形式へ逃がし、
    // 貼り付け直後に別の URL へ化けるのを防ぐ。
    const bareSafeHref = canRoundTripAsBareUrl(href);
    if (text.length === 0) {
      return bareSafeHref ? href : `[${escapeLinkText(href)}](${escapeLinkUrl(href)})`;
    }
    if (text === href && bareSafeHref) {
      return href;
    }
    return `[${escapeLinkText(text)}](${escapeLinkUrl(href)})`;
  }
  const inner = serializeChildren(element);
  return BLOCK_TAGS.has(tag) ? `${inner}\n` : inner;
}

/** 子ノード列を直列化して連結する */
function serializeChildren(node: Node): string {
  let out = "";
  node.childNodes.forEach((child) => {
    out += serializeNode(child);
  });
  return out;
}

/**
 * クリップボードの HTML 断片を、リンク（[テキスト](URL)）を保ったテキストへ変換する。
 * アンカーを 1 つも含まない場合は undefined を返す（ブラウザ既定の貼り付けに任せる）。
 */
export function htmlFragmentToLinkText(html: string): string | undefined {
  const doc = new DOMParser().parseFromString(html, "text/html");
  // 保持できる（http(s)）アンカーが 1 つも無ければ変換せず undefined を返し、
  // ブラウザ既定の貼り付け（text/plain）へ委ねる。
  if (!hasPreservableAnchor(doc)) {
    return undefined;
  }
  const text = serializeNode(doc.body);
  // 行ごとの前後空白を除去し、3 行以上の連続空行は 1 つの空行へ畳む。
  const normalized = text
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/gu, "\n\n")
    .trim();
  return normalized.length > 0 ? normalized : undefined;
}
